import React, { Fragment, useEffect, useState, useRef, useMemo } from "react"
import { Row, Card, CardHeader, CardBody, Button, Form, Col } from "reactstrap"
import Select from "react-select"
import TypeSelector from "./PageComponents/TypeSelector"
import MerchantForm from "./PageComponents/MerchantForm"
import WorkingHoursForm from "./PageComponents/WorkingHoursForm"
import ImageUpload from "./PageComponents/ImageUpload"
import { addMerchantDetails } from "../../../api-handle/merchantApi"
import { getTypes, getCategoriesByType, getSubCategories } from "../../../api-handle/utilApi"
import { useNavigate } from "react-router"
import { AlertCircle } from "react-feather"
import styles from "./PageComponents/WorkingHoursForm.module.scss"
import LocationPicker from "./PageComponents/LocationPicker"
import { toast } from "react-toastify"
import { removeFile, uploadSingleFile } from "../../../api-handle/fileApi"
import { sanitizeArabicEvent } from "../../../Services/arabicInput"

const genderOptions = [
  { value: "all", label: "All" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "boys", label: "Boys" },
  { value: "girls", label: "Girls" },
]

const resolveGenderSelection = (selected, previous) => {
  const selectedValues = selected.map((s) => s.value)
  const hadAll = previous.includes("all")
  const hasAll = selectedValues.includes("all")
  if (hasAll && !hadAll) return ["all"]
  if (hasAll && hadAll && selectedValues.length > 1) {
    return selectedValues.filter((v) => v !== "all")
  }
  return selectedValues
}

const ALL_GENDER_VALUES = ["male", "female", "boys", "girls"]

// A category's gender tags drive the default selection. Empty (genderless)
// or full coverage collapses to the "all" shorthand the selector already uses.
const genderFromCategory = (cat) => {
  const genders = (Array.isArray(cat?.gender) ? cat.gender : []).filter((g) =>
    ALL_GENDER_VALUES.includes(g)
  )
  if (!genders.length || genders.length === ALL_GENDER_VALUES.length) return ["all"]
  return genders
}

// Restrict the gender selector to what the chosen category supports. A
// genderless category (empty) or one tagged with every gender keeps the full
// list (including the "All" shorthand); a restricted category drops "All" and
// only offers its own genders.
const genderOptionsFromCategory = (cat) => {
  const genders = (Array.isArray(cat?.gender) ? cat.gender : []).filter((g) =>
    ALL_GENDER_VALUES.includes(g)
  )
  if (!genders.length || genders.length === ALL_GENDER_VALUES.length) return genderOptions
  return genderOptions.filter((opt) => genders.includes(opt.value))
}

// Gym/academy categories are genderless; their gender comes from the chosen
// SUB-CATEGORY instead. These mirror the category helpers above but read the
// sub-category's gender array.
const genderFromSubCategory = (sub) => genderFromCategory(sub)
const genderOptionsFromSubCategory = (sub) => genderOptionsFromCategory(sub)

const ERROR_FIELD_ORDER = [
  "type",
  "location",
  "gymName",
  "gymDescription",
  "gymCategory",
  "gymSubCategory",
  "gymLocation",
  "gymWorkingHours",
  "gymCoverImage",
  "gymGenderType",
  "academyName",
  "academyCategory",
  "academySubCategory",
  "academyDescription",
  "academyLocation",
  "academyWorkingHours",
  "academyCoverImage",
  "academyGenderType",
  "marketplaceGenderType",
  "marketplaceCategory",
  "documents",
]

const scrollToFirstError = (errors) => {
  const firstKey = ERROR_FIELD_ORDER.find((key) => errors[key])
  if (!firstKey) return
  const el = document.querySelector(`[data-error-field="${firstKey}"]`)
  if (!el) return
  el.scrollIntoView({ behavior: "smooth", block: "center" })
  const focusable = el.querySelector("input, select, textarea, [tabindex]:not([tabindex='-1'])")
  if (focusable && typeof focusable.focus === "function") {
    setTimeout(() => focusable.focus({ preventScroll: true }), 350)
  }
}

const MerchantDetails = () => {
  const [type, setType] = useState("")
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState("")
  // Types come from the API and drive the selectable list (via TypeSelector).
  const [types, setTypes] = useState([])
  // Categories/sub-categories keyed by type key, so nothing branches on the
  // literal "gym"/"academy"/"marketplace" strings.
  const [categoriesByType, setCategoriesByType] = useState({})
  const [subCategoriesByType, setSubCategoriesByType] = useState({})
  const loadedCategoryTypes = useRef(new Set())

  const typeIdByKey = useMemo(() => {
    const map = {}
    types.forEach((t) => { map[t.key] = t._id })
    return map
  }, [types])
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    gymDetail: { genderType: [] },
    academyDetail: { genderType: [] },
    marketplaceGenderType: [],
    marketplaceCategory: "",
    documents: [],
    contractDocument: "",
    type: [],
    location: {
      location: "",
      latitude: 0,
      longitude: 0,
    },
  })

  const validate = () => {
    const newErrors = {}

    if (!type || (Array.isArray(type) && type.length === 0)) {
      newErrors.type = "Please select a business type."
    }

    if (!formData.location?.location || !formData.location.latitude || !formData.location.longitude) {
      newErrors.location = "Please select a valid default location."
    }

    if (!formData.documents || formData.documents.length === 0) {
      newErrors.documents = "Upload at least one document for KYC."
    }

    if (type === "gym" || (Array.isArray(type) && type.includes("gym"))) {
      const gym = formData.gymDetail || {}
      if (!gym.name?.trim()) newErrors.gymName = "Gym name is required."
      if (!gym.description?.trim()) newErrors.gymDescription = "Gym description is required."
      if (!gym.location?.address) newErrors.gymLocation = "Gym location address is required."
      if (!gym.workingHours || gym.workingHours.length === 0) {
        newErrors.gymWorkingHours = "Please add working hours for the gym."
      }
      if (!gym.coverImage) newErrors.gymCoverImage = "Please upload a cover image for the gym."
      if (!gym.genderType || gym.genderType.length === 0) {
        newErrors.gymGenderType = "Please select gender type(s) for the gym."
      }
      if (!gym.category) newErrors.gymCategory = "Please select a category for the gym."
    }

    if (type === "academy" || (Array.isArray(type) && type.includes("academy"))) {
      const academy = formData.academyDetail || {}
      if (!academy.name?.trim()) newErrors.academyName = "Academy name is required."
      if (!academy.description?.trim()) newErrors.academyDescription = "Academy description is required."
      if (!academy.location?.address) newErrors.academyLocation = "Academy location address is required."
      if (!academy.workingHours || academy.workingHours.length === 0) {
        newErrors.academyWorkingHours = "Please add working hours for the academy."
      }
      if (!academy.coverImage) newErrors.academyCoverImage = "Please upload a cover image for the academy."
      if (!academy.genderType || academy.genderType.length === 0) {
        newErrors.academyGenderType = "Please select gender type(s) for the academy."
      }
      if (!academy.category) newErrors.academyCategory = "Please select a category for the academy."
      // if (!academy.subCategory) newErrors.academySubCategory = "Please select a sub-category for the academy."
    }

    if (type === "marketplace" || (Array.isArray(type) && type.includes("marketplace"))) {
      if (!formData.marketplaceGenderType || formData.marketplaceGenderType.length === 0) {
        newErrors.marketplaceGenderType = "Please select gender type(s) for the marketplace."
      }
      if (!formData.marketplaceCategory) {
        newErrors.marketplaceCategory = "Please select a category for the marketplace."
      }
    }

    setErrors(newErrors)
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      toast.dismiss()
      const firstKey = ERROR_FIELD_ORDER.find((k) => validationErrors[k])
      const firstMessage = firstKey ? validationErrors[firstKey] : "Please fill all the required fields."
      toast.error(firstMessage)
      scrollToFirstError(validationErrors)
      return
    }

    // Normalize type to array - handle both string and array, and flatten if nested
    let typeArray = []
    if (Array.isArray(type)) {
      // Flatten nested arrays if any
      typeArray = type.flat()
    } else if (type) {
      typeArray = [type]
    } else if (formData.type && Array.isArray(formData.type)) {
      typeArray = formData.type.flat()
    } else {
      toast.error("Please select at least one business type")
      return
    }
    
    // Remove duplicates and keep only types known to the backend (sourced from
    // the Types API rather than a hardcoded list).
    const allowedTypeKeys = types.map((t) => t.key)
    typeArray = [...new Set(typeArray.filter((t) => t && allowedTypeKeys.includes(t)))]
    
    // Build complete payload with all details
    // Note: LocationPicker stores the address under the `location` key, but the
    // backend Merchant schema expects `address`. Map it so admin KYC can see it.
    const defaultLocation = formData.location || {}
    const payload = {
      type: typeArray,
      documents: formData.documents || [],
      contractDocument: formData.contractDocument || "",
      location: {
        address: defaultLocation.address || defaultLocation.location || "",
        latitude: defaultLocation.latitude || 0,
        longitude: defaultLocation.longitude || 0,
      },
      logo: formData.logo || "",
      marketplaceName: formData.marketplaceName || "",
      marketplaceNameAr: formData.marketplaceNameAr || "",
    }

    // Include gymDetail if gym is selected - include ALL fields from formData
    if (typeArray.includes("gym")) {
      const gymDetail = formData.gymDetail || {}
      payload.gymDetail = {
        name: gymDetail.name || "",
        nameAr: gymDetail.nameAr || "",
        description: gymDetail.description || "",
        descriptionAr: gymDetail.descriptionAr || "",
        location: gymDetail.location || { latitude: "", longitude: "", address: "" },
        workingHours: Array.isArray(gymDetail.workingHours) ? gymDetail.workingHours : [],
        images: Array.isArray(gymDetail.images) ? gymDetail.images : [],
        videos: Array.isArray(gymDetail.videos) ? gymDetail.videos : [],
        coverImage: gymDetail.coverImage || "",
        genderType: Array.isArray(gymDetail.genderType) ? gymDetail.genderType : [],
        category: gymDetail.category || null,
        subCategory: gymDetail.subCategory || null,
      }
    } else {
      payload.gymDetail = {}
    }

    // Include academyDetail if academy is selected - include ALL fields from formData
    if (typeArray.includes("academy")) {
      const academyDetail = formData.academyDetail || {}
      payload.academyDetail = {
        name: academyDetail.name || "",
        nameAr: academyDetail.nameAr || "",
        description: academyDetail.description || "",
        descriptionAr: academyDetail.descriptionAr || "",
        location: academyDetail.location || { latitude: "", longitude: "", address: "" },
        workingHours: Array.isArray(academyDetail.workingHours) ? academyDetail.workingHours : [],
        images: Array.isArray(academyDetail.images) ? academyDetail.images : [],
        videos: Array.isArray(academyDetail.videos) ? academyDetail.videos : [],
        coverImage: academyDetail.coverImage || "",
        genderType: Array.isArray(academyDetail.genderType) ? academyDetail.genderType : [],
        category: academyDetail.category || null,
        subCategory: academyDetail.subCategory || null,
      }
    } else {
      payload.academyDetail = {}
    }

    // Marketplace account-level configuration
    if (typeArray.includes("marketplace")) {
      payload.marketplaceGenderType = Array.isArray(formData.marketplaceGenderType)
        ? formData.marketplaceGenderType
        : []
      payload.marketplaceCategory = formData.marketplaceCategory || null
    }

    if (payload.documents.length === 0) {
      alert("Upload Document For KYC")
      return
    }

    console.log("Registration payload:", JSON.stringify(payload, null, 2))
    console.log("FormData:", JSON.stringify(formData, null, 2))

    try {
      const response = await addMerchantDetails(payload)
      localStorage.setItem("isFirstTime", false)
      navigate("/pages/dashboard")
    } catch (error) {
      if (!error?.isSuccess) {
        setErrorMessage(error?.message)
        return
      }
      console.error("Error during registration:", error)
    }
  }

  // Step 1: only the list of types is fetched up-front. This builds the
  // key -> typeId map used to lazily load categories once a type is selected.
  const fetchTypes = async () => {
    try {
      const typesResponse = await getTypes()
      setTypes(typesResponse?.types || [])
    } catch (error) {
      console.log("Error fetching types:", error)
    }
  }

  // Step 2: load a single type's categories on demand (only once per type).
  const loadCategoriesForType = async (key) => {
    const typeId = typeIdByKey[key]
    if (!typeId || loadedCategoryTypes.current.has(key)) return
    loadedCategoryTypes.current.add(key)
    try {
      const response = await getCategoriesByType(typeId)
      const categories = response?.categories || []
      setCategoriesByType((prev) => ({ ...prev, [key]: categories }))
      
      // Auto-select first category for gym/academy
      if ((key === "gym" || key === "academy") && categories.length > 0) {
        const firstCategoryId = categories[0]._id
        setFormData((prev) => ({
          ...prev,
          [`${key}Detail`]: {
            ...prev[`${key}Detail`],
            category: firstCategoryId,
          },
        }))
        // Load subcategories for the first category - this will auto-select first subcategory and gender
        loadSubCategories(firstCategoryId, key)
      }
    } catch (error) {
      console.log("Error fetching categories:", error)
      loadedCategoryTypes.current.delete(key)
    }
  }

  // Auto-fill the gender selection from the picked category. For marketplace
  // the source of gender is the category; gym/academy categories are
  // genderless, so picking a gym/academy category only resets gender (the real
  // default comes from the sub-category via applySubCategoryGender).
  const applyCategoryGender = (categoryId, key) => {
    if (key === "marketplace") {
      const cat = (categoriesByType[key] || []).find((c) => c._id === categoryId)
      if (!cat) return
      const genderType = genderFromCategory(cat)
      setFormData((prev) => ({ ...prev, marketplaceGenderType: genderType }))
      return
    }
    // Gym/academy: category no longer drives gender; clear it until a
    // sub-category is chosen.
    setFormData((prev) => ({
      ...prev,
      [`${key}Detail`]: { ...prev[`${key}Detail`], genderType: [] },
    }))
  }

  // Auto-fill the gym/academy gender from the picked SUB-CATEGORY. The vendor
  // can still change it afterwards — this is a default, not a restriction.
  const applySubCategoryGender = (subCategoryId, key) => {
    const sub = (subCategoriesByType[key] || []).find((s) => s._id === subCategoryId)
    const genderType = sub ? genderFromSubCategory(sub) : []
    setFormData((prev) => ({
      ...prev,
      [`${key}Detail`]: { ...prev[`${key}Detail`], genderType },
    }))
  }

  // Gender options for a type's selector. Marketplace narrows by its selected
  // category; gym/academy narrow by their selected SUB-CATEGORY (their
  // categories are genderless). Falls back to the full list until the relevant
  // source is picked.
  const genderOptionsForType = (key) => {
    if (key === "marketplace") {
      const cat = (categoriesByType[key] || []).find(
        (c) => c._id === formData.marketplaceCategory
      )
      return cat ? genderOptionsFromCategory(cat) : genderOptions
    }
    const subCategoryId = formData[`${key}Detail`]?.subCategory
    const sub = (subCategoriesByType[key] || []).find((s) => s._id === subCategoryId)
    return sub ? genderOptionsFromSubCategory(sub) : genderOptions
  }

  const loadSubCategories = async (categoryId, target) => {
    if (!categoryId) {
      setSubCategoriesByType((prev) => ({ ...prev, [target]: [] }))
      return
    }
    try {
      const response = await getSubCategories(categoryId)
      const subcategories = response?.filteredData?.categories || []
      setSubCategoriesByType((prev) => ({ ...prev, [target]: subcategories }))
      
      // Auto-select first subcategory for gym/academy
      if ((target === "gym" || target === "academy") && subcategories.length > 0) {
        const firstSubCategory = subcategories[0]
        const firstSubCategoryId = firstSubCategory._id
        const genderType = genderFromSubCategory(firstSubCategory) // Use the fetched subcategory directly
        
        setFormData((prev) => ({
          ...prev,
          [`${target}Detail`]: {
            ...prev[`${target}Detail`],
            subCategory: firstSubCategoryId,
            genderType: genderType, // Set gender directly
          },
        }))
      }
    } catch (error) {
      console.log("Error fetching sub-categories:", error)
      setSubCategoriesByType((prev) => ({ ...prev, [target]: [] }))
    }
  }

  // On mount: fetch only the types.
  useEffect(() => {
    fetchTypes()
  }, [])

  // Step 3 (cascade): when a type is selected, fetch that type's categories.
  useEffect(() => {
    if (!Object.keys(typeIdByKey).length) return
    const selected = Array.isArray(type) ? type : type ? [type] : []
    selected.forEach((key) => loadCategoriesForType(key))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, typeIdByKey])

  return (
    <Fragment>
      <div
        style={{
          backgroundColor: "#e6f7ff",
          borderLeft: "5px solid #1890ff",
          padding: "12px 16px",
          marginBottom: "1rem",
          borderRadius: "4px",
          color: "#004085",
          fontSize: "0.95rem",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginInline: "20px",
          marginBlock: "20px",
        }}
      >
        <AlertCircle size={18} color="#1890ff" />
        Dashboard will be accessible once your business information is completed and approved.
      </div>

      <Card
        style={{
          marginInline: "20px",
          border: "1px solid #33bdbf",
        }}
      >
        <CardHeader>
          <h2>Add Your Business Details</h2>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit} noValidate>
            <div data-error-field="type">
              <TypeSelector type={type} setType={setType} types={types} />
              {errors.type && (
                <div className="text-danger small mt-1">{errors.type}</div>
              )}
            </div>

            {type && type.length > 0 && (
              <>
                <Row className="mt-4">
                  <Col lg={12} md={12}>
                    <h5 className="mb-2">Default Location</h5>
                    <div data-error-field="location">
                      <LocationPicker
                        locationData={formData.location}
                        setLocationData={(loc) =>
                          setFormData((prev) => ({
                            ...prev,
                            location: loc,
                          }))
                        }
                      />
                      {errors.location && (
                        <div className="text-danger small mt-1">{errors.location}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <MerchantForm
                  selectedTypes={type}
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  categoriesByType={categoriesByType}
                  subCategoriesByType={subCategoriesByType}
                  genderOptions={genderOptions}
                  genderOptionsForType={genderOptionsForType}
                  resolveGenderSelection={resolveGenderSelection}
                  onLoadSubCategories={(catId, t) => {
                    loadSubCategories(catId, t)
                    applyCategoryGender(catId, t)
                  }}
                  onSubCategoryChange={(subId, t) => applySubCategoryGender(subId, t)}
                />

                {/* NO GYM/ACADEMY GENDER HERE - MOVED TO MERCHANTFORM */}


                {/* Marketplace Extra Fields */}
                {Array.isArray(type) && type.includes("marketplace") && (
                  <Row className="mt-4">
                    {/* Marketplace Name EN */}
                    <Col lg={6} md={12}>
                      <h5 className="mb-2">Marketplace Name (EN)</h5>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.marketplaceName || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            marketplaceName: e.target.value,
                          }))
                        }
                        placeholder="Enter Marketplace Name"
                      />
                    </Col>

                    {/* Marketplace Name AR */}
                    <Col lg={6} md={12}>
                      <h5 className="mb-2">Marketplace Name (AR)</h5>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.marketplaceNameAr || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            marketplaceNameAr: sanitizeArabicEvent(e),
                          }))
                        }
                        placeholder="Enter Marketplace Name in Arabic"
                      />
                    </Col>

                    {/* Marketplace Category - MOVED BEFORE GENDER */}
                    <Col lg={6} md={12} className="mt-3">
                      <div data-error-field="marketplaceCategory">
                        <h5 className="mb-2">Category</h5>
                        <select
                          className="form-control"
                          value={formData.marketplaceCategory || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Find the category BEFORE setting state
                            const selectedCat = (categoriesByType.marketplace || []).find((c) => c._id === val);
                            const genderType = selectedCat ? genderFromCategory(selectedCat) : [];
                            
                            // Update state with both category and gender
                            setFormData((prev) => ({
                              ...prev,
                              marketplaceCategory: val,
                              marketplaceGenderType: genderType,
                            }));
                            
                            // Load subcategories
                            loadSubCategories(val, "marketplace");
                          }}
                          style={errors.marketplaceCategory ? { borderColor: "#d9534f" } : undefined}
                        >
                          <option value="">Select Category</option>
                          {(categoriesByType.marketplace || []).map((cat) => (
                            <option key={cat._id} value={cat._id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        {errors.marketplaceCategory && (
                          <div className="text-danger small mt-1">{errors.marketplaceCategory}</div>
                        )}
                      </div>
                    </Col>

                    {/* Marketplace Gender Type - Show upfront without subcategory */}
                    <Col lg={6} md={12} className="mt-3">
                      <div data-error-field="marketplaceGenderType">
                        <h5 className="mb-2">Gender Type</h5>
                        <Select
                          isMulti
                          isDisabled={!formData.marketplaceCategory}
                          placeholder={
                            formData.marketplaceCategory
                              ? "Select gender types..."
                              : "Select category first"
                          }
                          options={genderOptionsForType("marketplace")}
                          value={(formData.marketplaceGenderType || []).map((g) =>
                            genderOptions.find((opt) => opt.value === g)
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              marketplaceGenderType: resolveGenderSelection(
                                selected,
                                prev.marketplaceGenderType || []
                              ),
                            }))
                          }
                          styles={
                            errors.marketplaceGenderType
                              ? { control: (base) => ({ ...base, borderColor: "#d9534f" }) }
                              : undefined
                          }
                        />
                        {errors.marketplaceGenderType && (
                          <div className="text-danger small mt-1">{errors.marketplaceGenderType}</div>
                        )}
                      </div>
                    </Col>

                    {/* Marketplace Logo Upload */}
                    <Col lg={12} md={12} className="mt-4">
                      <h5 className="mb-2">Marketplace Logo</h5>
                      <div
                        className="border border-dashed rounded p-4 text-center position-relative"
                        style={{
                          borderColor: "#33bdbf",
                          backgroundColor: "#f8fdfd",
                          cursor: "pointer",
                          transition: "0.3s",
                        }}
                        onClick={() => document.getElementById("marketplaceLogoInput").click()}
                      >
                        {!formData.logo ? (
                          <div>
                            <i className="bi bi-upload" style={{ fontSize: "2rem", color: "#33bdbf" }}></i>
                            <p className="mt-2 mb-0 text-muted">Click or drag an image here to upload</p>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={`${process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL}/${formData.logo}`}
                              alt="Marketplace Logo"
                              style={{
                                width: "120px",
                                borderRadius: "10px",
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger mt-2"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await removeFile(formData.logo);
                                  setFormData((prev) => ({ ...prev, logo: "" }));
                                } catch (err) {
                                  console.error("Logo remove failed", err);
                                }
                              }}
                            >
                              Remove Logo
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Hidden File Input */}
                      <input
                        id="marketplaceLogoInput"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const response = await uploadSingleFile(file);
                            setFormData((prev) => ({
                              ...prev,
                              logo: response.fileName,
                            }));
                          } catch (err) {
                            console.error("Logo upload failed", err);
                          }
                        }}
                      />
                    </Col>
                  </Row>
                )}


                {formData.type
                  .filter((businessType) => businessType !== "marketplace")
                  .map((businessType) => (
                    <div
                      key={businessType}
                      data-error-field={`${businessType}WorkingHours`}
                    >
                      <WorkingHoursForm
                        businessType={businessType}
                        workingHours={formData[`${businessType}Detail`]?.workingHours || []}
                        setWorkingHours={(hours) =>
                          setFormData((prev) => ({
                            ...prev,
                            [`${businessType}Detail`]: {
                              ...prev[`${businessType}Detail`],
                              workingHours: hours,
                            },
                          }))
                        }
                      />
                      {errors[`${businessType}WorkingHours`] && (
                        <div className="text-danger small mt-1">
                          {errors[`${businessType}WorkingHours`]}
                        </div>
                      )}
                    </div>
                  ))}

                <ImageUpload formData={formData} setFormData={setFormData} type={type} errors={errors} />

                <Button color="primary" type="submit" className="mt-3">
                  Submit
                </Button>

                {errorMessage && (
                  <div className={styles.errorMessage}>
                    <AlertCircle size={16} className={styles.errorIcon} /> {errorMessage}
                  </div>
                )}
              </>
            )}
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  )
}

export default MerchantDetails
